import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Lightbulb, CheckCircle, ChevronRight, Loader } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const INDUSTRIES = ['hvac', 'mep', 'manufacturing', 'tender', 'sme'];
const TIMELINES = ['Immediately', '1-3 months', '3-6 months', '6+ months'];
const TIMELINES_AR = ['فوراً', '١-٣ أشهر', '٣-٦ أشهر', '٦ أشهر أو أكثر'];

export default function CustomSolutionModal({ isOpen, onClose }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const [step, setStep] = useState(1); // 1 = form, 2 = success
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    industry: '',
    challenge: '',
    timeline: '',
  });
  const [errors, setErrors] = useState({});

  const timelines = lang === 'ar' ? TIMELINES_AR : TIMELINES;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
    if (!form.challenge.trim() || form.challenge.trim().length < 20) e.challenge = true;
    return e;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          type: 'custom_solution',
          language: lang,
          source: 'custom_solution_modal',
        }),
      });
      setStep(2);
    } catch {
      // Still show success to not block user
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setForm({ name: '', company: '', email: '', phone: '', industry: '', challenge: '', timeline: '' });
    setErrors({});
    onClose();
  };

  const inputClass = (field) =>
    `w-full px-3.5 py-2.5 rounded-xl border text-sm transition-all outline-none focus:ring-2 focus:ring-primary/20 ${
      errors[field]
        ? 'border-error bg-red-50 focus:border-error'
        : 'border-border-light bg-white focus:border-primary'
    } ${isRTL ? 'text-right' : 'text-left'}`;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-[60] bg-midnight/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.93, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.93, y: 30 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            className={`fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none`}
          >
            <div
              className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg overflow-hidden pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-br from-midnight via-deep-surface to-secondary-surface px-6 py-5 overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-primary/15 blur-xl" />
                <div className="absolute bottom-0 left-8 w-16 h-16 rounded-full bg-accent/10 blur-lg" />

                <div className="relative flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                      <Lightbulb size={20} className="text-accent" />
                    </div>
                    <div>
                      <h2 className="text-white font-bold text-lg leading-tight">
                        {t('customSolution.title')}
                      </h2>
                      <p className="text-white/60 text-xs mt-0.5">Tuly Technology</p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>

                {step === 1 && (
                  <p className="relative text-white/70 text-sm mt-3 leading-relaxed">
                    {t('customSolution.subtitle')}
                  </p>
                )}
              </div>

              {/* Body */}
              <AnimatePresence mode="wait">
                {step === 1 ? (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    onSubmit={handleSubmit}
                    className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto"
                  >
                    {/* Row: Name + Company */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-body mb-1">
                          {t('customSolution.nameLabel')} <span className="text-error">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          placeholder={t('customSolution.namePlaceholder')}
                          className={inputClass('name')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-body mb-1">
                          {t('customSolution.companyLabel')}
                        </label>
                        <input
                          type="text"
                          value={form.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          placeholder={t('customSolution.companyPlaceholder')}
                          className={inputClass('company')}
                        />
                      </div>
                    </div>

                    {/* Row: Email + Phone */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-body mb-1">
                          {t('customSolution.emailLabel')} <span className="text-error">*</span>
                        </label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder={t('customSolution.emailPlaceholder')}
                          className={inputClass('email')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-body mb-1">
                          {t('customSolution.phoneLabel')}
                        </label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder={t('customSolution.phonePlaceholder')}
                          className={inputClass('phone')}
                        />
                      </div>
                    </div>

                    {/* Industry */}
                    <div>
                      <label className="block text-xs font-medium text-body mb-1">
                        {t('customSolution.industryLabel')}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {INDUSTRIES.map((ind) => (
                          <button
                            type="button"
                            key={ind}
                            onClick={() => handleChange('industry', ind)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                              form.industry === ind
                                ? 'bg-primary text-white border-primary'
                                : 'border-border-light text-body hover:border-primary/40 hover:text-primary'
                            }`}
                          >
                            {t(`contact.industries.${ind}`)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Challenge */}
                    <div>
                      <label className="block text-xs font-medium text-body mb-1">
                        {t('customSolution.challengeLabel')} <span className="text-error">*</span>
                      </label>
                      <textarea
                        value={form.challenge}
                        onChange={(e) => handleChange('challenge', e.target.value)}
                        placeholder={t('customSolution.challengePlaceholder')}
                        rows={4}
                        className={`${inputClass('challenge')} resize-none`}
                      />
                      {errors.challenge && (
                        <p className="text-xs text-error mt-0.5">
                          {lang === 'ar' ? 'يرجى وصف تحديك (٢٠ حرفاً على الأقل)' : 'Please describe your challenge (at least 20 characters)'}
                        </p>
                      )}
                    </div>

                    {/* Timeline */}
                    <div>
                      <label className="block text-xs font-medium text-body mb-1">
                        {t('customSolution.timelineLabel')}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {timelines.map((tl, i) => (
                          <button
                            type="button"
                            key={i}
                            onClick={() => handleChange('timeline', tl)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                              form.timeline === tl
                                ? 'bg-accent text-[#1A1A1A] border-accent'
                                : 'border-border-light text-body hover:border-accent/40'
                            }`}
                          >
                            {tl}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Submit */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                      >
                        {loading ? (
                          <>
                            <Loader size={16} className="animate-spin" />
                            {lang === 'ar' ? 'جاري الإرسال...' : 'Submitting...'}
                          </>
                        ) : (
                          <>
                            {t('customSolution.submit')}
                            <ChevronRight size={16} className={isRTL ? 'rotate-180' : ''} />
                          </>
                        )}
                      </button>
                    </div>
                  </motion.form>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="px-6 py-10 flex flex-col items-center text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
                      className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4"
                    >
                      <CheckCircle size={32} className="text-success" />
                    </motion.div>
                    <h3 className="text-heading font-bold text-xl mb-2">
                      {t('customSolution.successTitle')}
                    </h3>
                    <p className="text-body text-sm leading-relaxed mb-6">
                      {t('customSolution.successMsg')}
                    </p>
                    <button
                      onClick={handleClose}
                      className="px-6 py-2.5 rounded-xl bg-primary text-white font-medium text-sm hover:bg-primary-hover transition-colors"
                    >
                      {t('customSolution.close')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
